import { useMemo, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Layers, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { useHasPermission } from '@/hooks/usePermissions';
import {
  useAvailableEquipmentCategories,
  useDisableEquipmentCategory,
  useEnableEquipmentCategories,
  useEquipmentCategories,
} from '@/hooks/equipment/useEquipment';
import { groupEquipmentCategoryCatalog } from '@/components/organisms/equipment/equipmentCategoryCatalog';
import { CreateEquipmentCategoryDialog } from '@/components/organisms/equipment/CreateEquipmentCategoryDialog';

export const Route = createFileRoute('/_dashboard/dashboard/equipment-categories')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('equipment:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: EquipmentCategoriesPage,
});

function EquipmentCategoriesPage() {
  const canEdit = useHasPermission('equipment:edit');
  const enabledQuery = useEquipmentCategories();
  const availableQuery = useAvailableEquipmentCategories();
  const enableMutation = useEnableEquipmentCategories();
  const disableMutation = useDisableEquipmentCategory();

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [customCategoryOpen, setCustomCategoryOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const enabled = useMemo(() => enabledQuery.data ?? [], [enabledQuery.data]);
  const available = useMemo(() => availableQuery.data ?? [], [availableQuery.data]);

  const catalogGroups = useMemo(
    () => groupEquipmentCategoryCatalog(available, catalogSearch),
    [available, catalogSearch],
  );
  const visibleAddableIds = useMemo(
    () =>
      catalogGroups.flatMap(([, rows]) =>
        rows.filter((category) => !category.enabled).map((category) => category.id),
      ),
    [catalogGroups],
  );

  const enabledCount = enabled.length;
  const addableCount = available.filter((category) => !category.enabled).length;
  const existingTrades = useMemo(
    () => [...enabled, ...available].map((category) => category.trade),
    [available, enabled],
  );

  function setCatalogDialogOpen(nextOpen: boolean) {
    setCatalogOpen(nextOpen);
    if (!nextOpen) {
      setCatalogSearch('');
      setSelectedIds(new Set());
    }
  }

  function toggleCategory(categoryId: string, checked: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(categoryId);
      else next.delete(categoryId);
      return next;
    });
  }

  function selectVisible() {
    setSelectedIds((current) => new Set([...current, ...visibleAddableIds]));
  }

  function handleAddSelected() {
    if (selectedIds.size === 0) return;
    enableMutation.mutate([...selectedIds], {
      onSuccess: () => setCatalogDialogOpen(false),
    });
  }

  return (
    <div className="space-y-6 p-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <h1 className="text-lg font-semibold">Equipment Categories</h1>
          <p className="text-sm text-muted-foreground">
            Choose customer-site equipment that has a make/model, warranty, and related job history.
            Equipment types are then built within these categories.
          </p>
          {!availableQuery.isLoading && !availableQuery.isError ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {enabledCount} enabled · {addableCount} available to add
            </p>
          ) : null}
        </div>
        {canEdit ? (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              variant="outline"
              className="w-full shrink-0 sm:w-auto"
              onClick={() => setCustomCategoryOpen(true)}
            >
              <Plus className="mr-2 size-4" />
              Create custom category
            </Button>
            <Button
              className="w-full shrink-0 sm:w-auto"
              onClick={() => setCatalogOpen(true)}
              disabled={availableQuery.isLoading}
            >
              <Plus className="mr-2 size-4" />
              Choose categories
            </Button>
          </div>
        ) : null}
      </div>

      {customCategoryOpen ? (
        <CreateEquipmentCategoryDialog
          open
          onOpenChange={setCustomCategoryOpen}
          existingTrades={existingTrades}
        />
      ) : null}

      <Dialog open={catalogOpen} onOpenChange={setCatalogDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Choose equipment categories</DialogTitle>
            <DialogDescription>
              Select the installed assets your team services and tracks by make/model and warranty.
              Already-enabled categories stay checked; you can remove unused categories from the
              main list.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                placeholder="Search category, key, or trade…"
                aria-label="Search equipment categories"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectVisible}
                disabled={visibleAddableIds.length === 0}
              >
                Select all results
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                disabled={selectedIds.size === 0}
              >
                Clear new selections
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border bg-muted/20 p-3">
            {availableQuery.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 12 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 w-full" />
                ))}
              </div>
            ) : availableQuery.isError ? (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <AlertCircle className="size-8 text-destructive" />
                <p className="text-sm text-muted-foreground">
                  Failed to load the category catalog.
                </p>
                <Button variant="outline" size="sm" onClick={() => void availableQuery.refetch()}>
                  <RefreshCw className="mr-2 size-3.5" />
                  Retry
                </Button>
              </div>
            ) : catalogGroups.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No categories match “{catalogSearch}”.
              </div>
            ) : (
              <div className="space-y-5">
                {catalogGroups.map(([trade, rows]) => (
                  <section key={trade}>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="text-sm font-semibold">{trade}</h3>
                      <Badge variant="secondary">{rows.length}</Badge>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {rows.map((category) => {
                        const checked = category.enabled || selectedIds.has(category.id);
                        return (
                          <label
                            key={category.id}
                            htmlFor={`equipment-category-${category.id}`}
                            className={`flex min-h-16 items-start gap-3 rounded-md border p-3 transition-colors ${
                              category.enabled
                                ? 'border-primary/25 bg-primary/5'
                                : checked
                                  ? 'border-primary bg-primary/5'
                                  : 'cursor-pointer bg-background hover:border-primary/40 hover:bg-accent/40'
                            }`}
                          >
                            <Checkbox
                              id={`equipment-category-${category.id}`}
                              checked={checked}
                              disabled={category.enabled}
                              onCheckedChange={(next) => toggleCategory(category.id, next === true)}
                              aria-label={`${category.enabled ? 'Enabled' : 'Enable'} ${category.name}`}
                              className="mt-0.5"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 text-sm font-medium">
                                {category.name}
                                {category.enabled ? (
                                  <CheckCircle2 className="size-3.5 shrink-0 text-primary" />
                                ) : null}
                              </span>
                              <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">
                                {category.key}
                              </span>
                              {category.defaultLifespanYears ? (
                                <span className="mt-1 block text-xs text-muted-foreground">
                                  Typical life: {category.defaultLifespanYears} years
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {enabledCount} already enabled · {selectedIds.size} selected to add
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCatalogDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddSelected}
                disabled={selectedIds.size === 0 || enableMutation.isPending}
              >
                {enableMutation.isPending
                  ? 'Adding…'
                  : selectedIds.size === 0
                    ? 'Add selected categories'
                    : `Add ${selectedIds.size} categor${selectedIds.size === 1 ? 'y' : 'ies'}`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {enabledQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : enabledQuery.isError ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="size-8 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load categories.</p>
              <Button variant="outline" size="sm" onClick={() => void enabledQuery.refetch()}>
                <RefreshCw className="mr-2 size-3.5" />
                Retry
              </Button>
            </div>
          ) : enabled.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Layers className="size-8 text-muted-foreground" />
              <p className="text-sm font-medium">No categories yet</p>
              <p className="text-sm text-muted-foreground">
                Add the equipment categories your company works with to get started.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Trade</TableHead>
                  <TableHead>Typical lifespan</TableHead>
                  {canEdit ? <TableHead className="w-[1%]" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {enabled.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {c.name}
                        {c.isSystem === false ? <Badge variant="outline">Custom</Badge> : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{c.trade}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.defaultLifespanYears ? `${c.defaultLifespanYears} yrs` : '—'}
                    </TableCell>
                    {canEdit ? (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${c.name}`}
                          disabled={disableMutation.isPending}
                          onClick={() => disableMutation.mutate(c.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
